import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import { PageContainer, PageHeader } from "../components/PageHeader";
import SignageTemplateDialog from "../components/SignageTemplateDialog";
import SignageSlideDialog from "../components/SignageSlideDialog";
import { PermissionGate, usePermission } from "../auth/access";
import { tokens } from "../theme";
import { describeError, useToast } from "../components/toast";
import { errorText } from "../utils/apiError";
import { foyerApi, signageScopeKey, type SignageScopeRef } from "../http/foyer";
import { isOwnedBy, ownerLabel } from "../utils/signageItems";
import type {
  SignageTemplate,
  SignageTemplateInstance,
} from "@shared/types/signage-template";

/**
 * Templates & slides - the two halves of the signage template system, on one
 * page because they are one job seen from two sides.
 *
 * The TABS are not cosmetic: they are the two tiers. A user with
 * `canManageSignage` and nothing else sees Slides, can build one, and finds the
 * Templates tab read-only - no New, no Edit, no Delete. A designer sees both.
 * That is the same shape the Channels page uses for `BE-40`: a customer browses
 * the catalogue and nothing on their screen suggests they could add to it.
 */

const SEPARATOR = " · ";

export default function SignageTemplatesPage({
  premiseId,
  companyId,
}: {
  /** Exactly one is set: a location, or the whole customer. */
  premiseId?: string;
  companyId?: string;
}) {
  const toast = useToast();
  const { t } = useTranslation(["signage", "common", "glossary"]);
  const scope: SignageScopeRef = premiseId
    ? { kind: "premise", premiseId }
    : { kind: "company", companyId: companyId ?? "" };
  const scopeIdent = signageScopeKey(scope);
  const canDesign = usePermission("signage-templates", "edit");

  const [tab, setTab] = useState<"slides" | "templates">("slides");
  const [templates, setTemplates] = useState<SignageTemplate[]>([]);
  const [slides, setSlides] = useState<SignageTemplateInstance[]>([]);
  const [tick, setTick] = useState(0);

  const [templateOpen, setTemplateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<SignageTemplate | null>(null);
  const [slideOpen, setSlideOpen] = useState(false);
  const [editingSlide, setEditingSlide] =
    useState<SignageTemplateInstance | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void Promise.all([
      foyerApi.listSignageTemplatesIn(scope),
      foyerApi.listSignageSlidesIn(scope),
    ])
      .then(([t, s]) => {
        if (!alive) return;
        setTemplates(t);
        setSlides(s);
      })
      .catch(() => {
        if (!alive) return;
        setTemplates([]);
        setSlides([]);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeIdent, tick]);

  const byId = useMemo(
    () => new Map(templates.map((t) => [t.id, t] as const)),
    [templates]
  );

  /*
   * Both saves resolve true on success and leave closing to the surface: it
   * slides (or fades) away first, then calls onClose. Closing it here would
   * make the drawer vanish instead.
   */
  const saveTemplate = async (
    patch: Partial<SignageTemplate>
  ): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      if (editingTemplate) {
        await foyerApi.updateSignageTemplateIn(
          scope,
          editingTemplate.id,
          patch
        );
      } else {
        await foyerApi.createSignageTemplateIn(scope, patch);
      }
      setTick((t) => t + 1);
      toast.success(
        editingTemplate
          ? t("signageTemplatesPage.templateUpdated", {
              name: patch.name ?? editingTemplate.name,
            })
          : t("signageTemplatesPage.templateCreated", {
              name: patch.name ?? "",
            })
      );
      return true;
    } catch (err) {
      setError(errorText(err, t));
      const name = patch.name ?? editingTemplate?.name;
      toast.error(
        name
          ? describeError(t, "saveSignageTemplateNamed", err, { name })
          : describeError(t, "saveSignageTemplate", err)
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveSlide = async (body: {
    name: string;
    templateId: string;
    values: Record<string, string>;
  }): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      if (editingSlide) {
        await foyerApi.updateSignageSlideIn(scope, editingSlide.id, {
          name: body.name,
          values: body.values,
        });
      } else {
        await foyerApi.createSignageSlideIn(scope, body);
      }
      setTick((t) => t + 1);
      toast.success(
        editingSlide
          ? t("signageTemplatesPage.slideUpdated", { name: body.name })
          : t("signageTemplatesPage.slideCreated", { name: body.name })
      );
      return true;
    } catch (err) {
      setError(errorText(err, t));
      toast.error(describeError(t, "saveSlide", err, { name: body.name }));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const removeTemplate = async (id: string) => {
    try {
      const label = templates.find((tpl) => tpl.id === id)?.name ?? "";
      await foyerApi.deleteSignageTemplateIn(scope, id);
      setTick((n) => n + 1);
      toast.success(t("signageTemplatesPage.templateDeleted", { name: label }));
    } catch (err) {
      const name = templates.find((tpl) => tpl.id === id)?.name;
      toast.error(
        name
          ? describeError(t, "deleteSignageTemplateNamed", err, { name })
          : describeError(t, "deleteSignageTemplate", err)
      );
      /*
       * A 409 means slides are built from it. The server counts them and
       * refuses, because deleting would silently empty every one of those
       * slides - and they sit in other people's playlists, possibly at another
       * layer. Said here rather than swallowed.
       */
      setError(t("signageTemplatesPage.templateInUse"));
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title={t("signageTemplatesPage.title")}
        subtitle={t("signageTemplatesPage.subtitle")}
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as typeof tab)}
        sx={{ mb: 2 }}
      >
        <Tab
          value="slides"
          label={t("signageTemplatesPage.tabSlides", { count: slides.length })}
        />
        <Tab
          value="templates"
          label={t("signageTemplatesPage.tabTemplates", {
            count: templates.length,
          })}
        />
      </Tabs>

      {error && (
        <Typography variant="bodySm" sx={{ mb: 2, color: tokens.error }}>
          {error}
        </Typography>
      )}

      {tab === "slides" ? (
        <Stack gap={1}>
          <Stack direction="row" justifyContent="flex-end">
            <PermissionGate resource="signage" level="edit">
              <Button
                size="small"
                startIcon={<AddIcon />}
                disabled={templates.length === 0}
                onClick={() => {
                  setEditingSlide(null);
                  setSlideOpen(true);
                }}
              >
                {t("signageTemplatesPage.newSlide")}
              </Button>
            </PermissionGate>
          </Stack>

          {slides.length === 0 ? (
            <Empty>
              {templates.length === 0
                ? // Offer the action that is POSSIBLE - the rule the playlist
                  // dialog already follows when a scope has no playlists.
                  t("signageTemplatesPage.noTemplatesToFill")
                : t("signageTemplatesPage.noSlides")}
            </Empty>
          ) : (
            slides.map((slide) => {
              const owned = isOwnedBy(slide, scope);
              return (
                <Row key={slide.id}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "fontWeightSemibold" }}
                    >
                      {slide.name}
                    </Typography>
                    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                      {byId.get(slide.templateId)?.name ??
                        t("signageTemplatesPage.deletedTemplate")}
                    </Typography>
                  </Box>
                  {!owned && <Chip size="small" label={ownerLabel(t, slide)} />}
                  {owned && (
                    <>
                      <PermissionGate
                        resource="signage/slide-content"
                        level="edit"
                      >
                        <IconButton
                          size="small"
                          aria-label={t("signageTemplatesPage.editNamed", {
                            name: slide.name,
                          })}
                          onClick={() => {
                            setEditingSlide(slide);
                            setSlideOpen(true);
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </PermissionGate>
                      <PermissionGate resource="signage" level="edit">
                        <IconButton
                          size="small"
                          aria-label={t("signageTemplatesPage.deleteNamed", {
                            name: slide.name,
                          })}
                          onClick={() => {
                            if (
                              !window.confirm(
                                t("signageTemplatesPage.confirmDeleteSlide", {
                                  name: slide.name,
                                })
                              )
                            )
                              return;
                            void foyerApi
                              .deleteSignageSlideIn(scope, slide.id)
                              .then(() => {
                                setTick((n) => n + 1);
                                toast.success(
                                  t("signageTemplatesPage.slideDeleted", {
                                    name: slide.name,
                                  })
                                );
                              })
                              .catch((err) =>
                                toast.error(
                                  describeError(t, "deleteSlide", err, {
                                    name: slide.name,
                                  })
                                )
                              );
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </PermissionGate>
                    </>
                  )}
                </Row>
              );
            })
          )}
        </Stack>
      ) : (
        <Stack gap={1}>
          {canDesign && (
            <Stack direction="row" justifyContent="flex-end">
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingTemplate(null);
                  setTemplateOpen(true);
                }}
              >
                {t("signageTemplatesPage.newTemplate")}
              </Button>
            </Stack>
          )}

          {templates.length === 0 ? (
            <Empty>
              {canDesign
                ? t("signageTemplatesPage.noTemplatesDesign")
                : t("signageTemplatesPage.noTemplatesReadOnly")}
            </Empty>
          ) : (
            templates.map((tpl) => {
              const owned = isOwnedBy(tpl, scope);
              const used = slides.filter((s) => s.templateId === tpl.id).length;
              const summary = [
                tpl.canvas.width > tpl.canvas.height
                  ? t("common:labels.landscape")
                  : t("common:labels.portrait"),
                t("signageTemplatesPage.fieldCount", {
                  count: tpl.fields.length,
                }),
                used > 0 && t("glossary:slideCount", { count: used }),
              ]
                .filter(Boolean)
                .join(SEPARATOR);
              return (
                <Row key={tpl.id}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "fontWeightSemibold" }}
                    >
                      {tpl.name}
                    </Typography>
                    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                      {summary}
                    </Typography>
                  </Box>
                  {!owned && <Chip size="small" label={ownerLabel(t, tpl)} />}
                  {canDesign && owned && (
                    <>
                      <IconButton
                        size="small"
                        aria-label={t("signageTemplatesPage.editNamed", {
                          name: tpl.name,
                        })}
                        onClick={() => {
                          setEditingTemplate(tpl);
                          setTemplateOpen(true);
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={t("signageTemplatesPage.deleteNamed", {
                          name: tpl.name,
                        })}
                        onClick={() => void removeTemplate(tpl.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  {canDesign && !owned && (
                    // Borrowed records offer Duplicate, never Edit - the rule
                    // every other signage surface follows. Editing a chain's
                    // design from one hotel would change what its siblings show.
                    <Tooltip title={t("signageTemplatesPage.makeCopy")}>
                      <IconButton
                        size="small"
                        aria-label={t("signageTemplatesPage.duplicateNamed", {
                          name: tpl.name,
                        })}
                        onClick={() => {
                          setEditingTemplate(null);
                          setTemplateOpen(true);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Row>
              );
            })
          )}
        </Stack>
      )}

      <SignageTemplateDialog
        key={editingTemplate?.id ?? "new-template"}
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        template={editingTemplate}
        onSave={saveTemplate}
        saving={saving}
        error={error}
      />
      <SignageSlideDialog
        key={editingSlide?.id ?? "new-slide"}
        open={slideOpen}
        onClose={() => setSlideOpen(false)}
        slide={editingSlide}
        templates={templates}
        onSave={saveSlide}
        saving={saving}
        error={error}
      />
    </PageContainer>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{
        p: 1.5,
        border: `1px solid ${tokens.divider}`,
        borderRadius: "8px",
        bgcolor: tokens.surface,
      }}
    >
      {children}
    </Stack>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        border: `1px dashed ${tokens.divider}`,
        borderRadius: "10px",
        p: 4,
        textAlign: "center",
      }}
    >
      <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
        {children}
      </Typography>
    </Box>
  );
}
